import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import HeroTitle from "@features/tools/components/tool_components/HeroTitle";
import ErrorComponent from "@features/tools/components/tool_components/ErrorComponent";
import { Text } from "@core/text";
import Pagination from "@core/pagination";
import logo from "@packages/assets/logo.png";
import { Button } from "@core/button";
import { Input } from "@core/input";
import { Package, Globe, Lock } from "lucide-react";
import LoadingContainer from "@packages/components/Loading";
import { useNavigate } from "react-router-dom";
import {
  getMindmapGenerator,
  useCreateMindmapGenerator,
  useGetMindmapGenerator,
  useGetUserMindmapGenerators,
} from "@packages/api/madlenAPI";
import { useToast } from "@packages/contexts/ToastContext";
import { getEnvironment } from "@packages/utils";
import Card from "@features/tools/components/card";
import CourseAndAudianceInput, {
  processPrompt,
} from "@features/tools/components/create-modal/CourseAndAudianceInput";
import { firstTopicOfCourse } from "@packages/utils";
import GradeSelector from "@features/tools/components/grade-selector";
import CourseSelector from "@features/assignments/AssignmentGenerator/components/course-selector";
import {
  BaseChatDtoCourse,
  GetTopicsCourseType,
  TransferActivityCreateRequestDtoGenerationType,
} from "@packages/api/madlenAPI.schemas";
import { UnitTopicSelector } from "@packages/components/unit-topic-selector";
import { UnitOutcomeSelector } from "@features/assignments/AssignmentGenerator/components/unit-outcome-selector";
import { CourseData } from "@packages/constant/CourseData";
import SimpleLoading from "@core/loading/SimpleLoading";

export default function MindMapPage() {
  const { t, i18n } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 6;
  const [createPrompt, setCreatePrompt] = useState("");
  const [isCreatingMindMap, setIsCreatingMindMap] = useState(false);
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [gradeLevel, setGradeLevel] = useState<number>(8);
  const [courseName, setCourseName] = useState<GetTopicsCourseType>(
    i18n.language === "tr" ? "HISTORY" : "AP_2025_WORLD_HISTORY_MODERN",
  );
  const [isPublic, setIsPublic] = useState<boolean>(true);
  const [isTopicSelection, setIsTopicSelection] = useState(false);
  const [isOutcomeSelection, setIsOutcomeSelection] = useState(
    i18n.language === "tr",
  );
  const [selectedTopics, setSelectedTopics] = useState<number[]>([]);
  const [selectedOutcomes, setSelectedOutcomes] = useState<number[]>([]);
  const [selectedMaarifOutcomes, setSelectedMaarifOutcomes] = useState<
    number[]
  >([]);
  const [isMaarif, setIsMaarif] = useState(() => {
    const savedValue = localStorage.getItem("isMaarifEnabled");
    return savedValue ? JSON.parse(savedValue) : false;
  });
  const gradeAsNumber = Number(gradeLevel);

  const placeholderTopic = useMemo(() => {
    return firstTopicOfCourse(Number(gradeLevel), courseName);
  }, [gradeLevel, courseName]);

  const steps = useMemo(() => {
    if (i18n.language === "tr")
      return [
        "Konu analiz ediliyor ve bilgi toplanıyor...",
        "Görsel yapı oluşturuluyor...",
        "Kavramlar ve bağlantılar düzenleniyor...",
        "Zihin haritası oluşturma işlemi tamamlanıyor...",
        "Ses oluşturma işlemi tamamlanıyor...",
      ];
    if (i18n.language === "fr")
      return [
        "Analyse du sujet et collecte d'informations...",
        "Création de la structure visuelle...",
        "Organisation des concepts et connexions...",
        "Finalisation de la génération de la carte mentale...",
        "Génération de l'audio...",
      ];
    if (i18n.language === "de")
      return [
        "Thema analysieren und Informationen sammeln...",
        "Visuelle Struktur erstellen...",
        "Konzepte und Verbindungen organisieren...",
        "Mind Map-Generierung abschließen...",
        "Audio generieren...",
      ];
    return [
      "Analyzing topic and gathering information...",
      "Creating visual structure...",
      "Organizing concepts and connections...",
      "Finalizing mind map generation...",
      "Generating audio...",
    ];
  }, [i18n.language]);

  const {
    data: mindMapsData,
    isLoading: isLoadingMindMaps,
    error: errorMindMaps,
    refetch: refetchMindMaps,
  } = useGetUserMindmapGenerators({
    page: currentPage,
    size: pageSize,
  });

  const { data: creatingMindMap, mutateAsync: createMindMap } =
    useCreateMindmapGenerator();

  useGetMindmapGenerator(Number(creatingMindMap?.id), {
    query: {
      enabled: !!creatingMindMap?.id,
      refetchInterval: (query) => {
        if (query?.state?.data?.status === "FAILED") {
          return false;
        }
        if (
          !query?.state?.data?.nodes ||
          query?.state?.data?.nodes.length === 0
        ) {
          return 5000;
        }
        if (!query?.state?.data?.imageUrl) {
          return 3000;
        }
        if (query?.state?.data?.status === "READY") {
          setIsCreatingMindMap(false);
          navigate(`/content/mindmaps/${creatingMindMap?.id}`);
        }
        return false;
      },
      queryFn: async () => {
        const response = await getMindmapGenerator(Number(creatingMindMap?.id));
        if (response?.imageUrl) {
          // setIsImageLoading(false);
        } else {
          // setIsImageLoading(true);
        }

        if (response?.status === "FAILED") {
          setCreatePrompt("");

          addToast({
            title: t("mindmap.error.generationFailed"),
            description: t("mindmap.error.tryAgain"),
            variant: "error",
            duration: 5000,
          });
          setIsCreatingMindMap(false);
        }

        return response;
      },
    },
  });

  const handleCreateMindMap = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate inputs based on selected mode
    if (isTopicSelection && selectedTopics.length === 0) {
      addToast({
        title: t("notifications.assignment.emptyTopics"),
        variant: "error",
        duration: 3000,
      });
      return;
    }

    if (
      isOutcomeSelection &&
      selectedOutcomes.length === 0 &&
      selectedMaarifOutcomes.length === 0
    ) {
      addToast({
        title: t("notifications.assignment.emptyOutcomes"),
        variant: "error",
        duration: 3000,
      });
      return;
    }

    if (!isTopicSelection && !isOutcomeSelection && !createPrompt.trim()) {
      addToast({
        title: t("notifications.assignment.emptyPrompt"),
        variant: "error",
        duration: 3000,
      });
      return;
    }

    setIsCreatingMindMap(true);
    try {
      // Determine the generation type based on selection mode
      let generationType =
        TransferActivityCreateRequestDtoGenerationType.PROMPT;
      if (isTopicSelection) {
        generationType =
          TransferActivityCreateRequestDtoGenerationType.TOPIC_SELECTION;
      } else if (isOutcomeSelection) {
        generationType =
          TransferActivityCreateRequestDtoGenerationType.MAARIF_LEARNING_OUTCOME;
      }

      await createMindMap({
        data: {
          prompt: createPrompt,
          environment: getEnvironment(),
          fromSuperTool: false,
          gradeLevel,
          courseName,
          generationType,
          isPublic,
        },
      });
      setCreatePrompt("");
    } catch (err) {
      console.error("Failed to create mind map:", err);
      setIsCreatingMindMap(false);
    }
  };

  const handleSelectMindMap = (id: number) => {
    navigate(`/content/mindmaps/${id}`);
  };

  const handlePageChange = (newPage: number) => {
    if (
      newPage > 0 &&
      (!mindMapsData?.totalPages || newPage <= mindMapsData?.totalPages)
    ) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 400, behavior: "smooth" });
    }
  };

  const shouldShowPagination =
    !errorMindMaps &&
    mindMapsData?.totalPages &&
    mindMapsData?.totalPages > 1 &&
    !isLoadingMindMaps;

  // Handle grade change
  const handleGradeChange = (grade: number) => {
    const availableCourses = Object.entries(CourseData).filter(([, value]) =>
      value.visible(grade),
    );
    const firstAvailableCourse =
      availableCourses[0]?.[0] as GetTopicsCourseType;
    const doesPreviousCourseExist = availableCourses.some(
      ([key]) => key === courseName,
    );
    setSelectedTopics([]);
    setSelectedOutcomes([]);
    setSelectedMaarifOutcomes([]);
    const isNewGradeJunior = grade >= 1 && grade <= 4;
    if (isNewGradeJunior || !firstAvailableCourse) {
      setIsTopicSelection(false);
      setIsOutcomeSelection((prev) => {
        return prev && firstAvailableCourse ? i18n.language === "tr" : false;
      });
    }
    setGradeLevel(grade);
    setCourseName(
      doesPreviousCourseExist
        ? courseName
        : (firstAvailableCourse ?? "UNKNOWN"),
    );
  };

  // Helper to determine if grade is junior (1-4)
  const isJuniorGrade = gradeAsNumber >= 1 && gradeAsNumber <= 4;

  if (isLoadingMindMaps && !mindMapsData) {
    return <SimpleLoading fastLoading={true} />;
  }

  if (errorMindMaps) {
    return (
      <ErrorComponent
        title={t("mindmap.error.fetchTitle")}
        refetch={refetchMindMaps}
        description={t("mindmap.error.fetchDescription")}
        retryButton={t("mindmap.error.retryButton")}
      />
    );
  }

  return (
    <div className="min-h-screen px-4 py-8 bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-blue-900 ">
      <HeroTitle
        title={t("sidebar.menu.toolbox.mindmap")}
        description={t("mindmap.description")}
        titleStyle="bg-gradient-to-r from-blue-600 via-teal-600 to-cyan-600 dark:from-blue-400 dark:via-teal-400 dark:to-cyan-400"
        titleBackgroundStyle="bg-gradient-to-r from-blue-50/30 to-teal-50/30 dark:from-blue-900/30 dark:to-teal-900/30"
      />

      <div className="bg-white/30 dark:bg-gray-800/30 backdrop-blur-md rounded-xl p-8 shadow-lg border border-white/20 dark:border-gray-700/20 relative mx-auto mb-12">
        <div className="relative generate-mindmap-form">
          <div className="space-y-4 sm:space-y-8">
            {!isCreatingMindMap ? (
              <div className="space-y-4">
                <div className="space-y-4">
                  <div className="space-y-2 flex flex-col items-start">
                    <label className="inline-block text-sm font-medium text-blue-700 dark:text-blue-300">
                      {t("hooks.form.gradeLevel")}
                    </label>
                    <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="grid grid-cols-4 gap-3">
                        <GradeSelector
                          selectedGrade={gradeLevel}
                          setSelectedGrade={handleGradeChange}
                        />
                      </div>
                      <div className="flex flex-col items-center gap-4 p-4 bg-white/50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700 backdrop-blur-sm">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-4">
                            <Button
                              type="button"
                              onClick={() => setIsPublic(true)}
                              variant="solid"
                              size="md"
                              rounded="lg"
                              textColor={
                                isPublic
                                  ? "text-white"
                                  : "text-gray-600 dark:text-gray-400"
                              }
                              bgColor={
                                isPublic
                                  ? "bg-gradient-to-r from-blue-600 to-teal-600 shadow-lg"
                                  : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                              }
                              leftIcon={<Globe size={16} />}
                            >
                              <span className="text-sm font-medium">
                                {t("hooks.form.public")}
                              </span>
                            </Button>

                            <Button
                              type="button"
                              onClick={() => setIsPublic(false)}
                              variant="solid"
                              size="md"
                              rounded="lg"
                              textColor={
                                !isPublic
                                  ? "text-white"
                                  : "text-gray-600 dark:text-gray-400"
                              }
                              bgColor={
                                !isPublic
                                  ? "bg-gradient-to-r from-blue-600 to-teal-600 shadow-lg"
                                  : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                              }
                              leftIcon={<Lock size={16} />}
                            >
                              <span className="text-sm font-medium">
                                {t("hooks.form.private")}
                              </span>
                            </Button>
                          </div>
                        </div>
                        <div className="flex-1 flex items-center justify-end">
                          <div
                            className={`px-3 py-1 rounded-full text-xs font-medium ${
                              isPublic
                                ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                                : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
                            }`}
                          >
                            {isPublic
                              ? t("hooks.form.publicDescription")
                              : t("hooks.form.privateDescription")}
                          </div>
                        </div>
                        {/* Maarif Toggle - Only show for Turkish language */}
                        {i18n.language === "tr" && (
                          <div className="flex items-center justify-center mt-4 p-2 bg-white/50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700">
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isMaarif}
                                onChange={(e) => {
                                  setIsMaarif(e.target.checked);
                                  // Clear outcomes when Maarif changes
                                  setSelectedOutcomes([]);
                                  setSelectedMaarifOutcomes([]);
                                }}
                                className="sr-only peer"
                              />
                              <div
                                className={`w-11 h-6 rounded-full peer relative transition-colors duration-300
                                ${isMaarif ? "bg-blue-600 dark:bg-blue-600" : "bg-gray-200 dark:bg-gray-700"}
                                peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800`}
                              ></div>
                              <div
                                className={`absolute h-5 w-5 rounded-full bg-white border border-gray-300 
                                transition-transform duration-300 top-[2px] start-[2px]
                                ${isMaarif ? "translate-x-5 rtl:-translate-x-5" : ""}`}
                              ></div>
                              <span className="ms-3 text-sm font-medium text-gray-900 dark:text-gray-300">
                                Türkiye Yüzyılı Maarif Modeli
                              </span>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {gradeAsNumber !== -1 && (
                    <div className="space-y-2 flex flex-col items-start">
                      <label className="inline-block text-sm font-medium text-blue-700 dark:text-blue-300">
                        {t("hooks.form.courseName")}
                      </label>
                      <div className="w-full grid grid-cols-2 md:grid-cols-4 gap-3">
                        <CourseSelector
                          selectedGrade={gradeLevel}
                          selectedCourse={courseName}
                          setSelectedCourse={(course) => {
                            setCourseName(course);
                            setSelectedTopics([]);
                            setSelectedOutcomes([]);
                            setSelectedMaarifOutcomes([]);
                          }}
                          filteredCourses={
                            isOutcomeSelection
                              ? isMaarif
                                ? [GetTopicsCourseType.DIGITAL_CITIZENSHIP]
                                : [
                                    GetTopicsCourseType.ENGLISH,
                                    GetTopicsCourseType.DIGITAL_CITIZENSHIP,
                                  ]
                              : []
                          }
                        />
                      </div>
                    </div>
                  )}
                  {gradeLevel === -1 && (
                    <CourseAndAudianceInput
                      input={{
                        courseName: courseName,
                        prompt: createPrompt,
                      }}
                      setInput={(input) => {
                        setCreatePrompt(input.prompt);
                      }}
                      labelClass={"text-blue-700 dark:text-blue-300"}
                    />
                  )}
                </div>

                {/* Selection Mode Buttons */}
                {gradeLevel !== -1 && (
                  <div className="flex flex-col md:flex-row flex-wrap justify-center gap-4">
                    <Button
                      type="button"
                      className="flex-1"
                      onClick={() => {
                        setIsTopicSelection(false);
                        setIsOutcomeSelection(false);
                        setCreatePrompt("");
                        setSelectedTopics([]);
                        setSelectedOutcomes([]);
                        setSelectedMaarifOutcomes([]);
                      }}
                      variant="solid"
                      size="md"
                      rounded="lg"
                      textColor={
                        !isTopicSelection && !isOutcomeSelection
                          ? "text-white"
                          : "text-gray-600 dark:text-gray-400"
                      }
                      bgColor={
                        !isTopicSelection && !isOutcomeSelection
                          ? "bg-gradient-to-r from-blue-600 to-teal-600 shadow-lg"
                          : "bg-gray-100 dark:bg-gray-700"
                      }
                    >
                      {t("common.freeInput")}
                    </Button>
                    {!isJuniorGrade ? (
                      <>
                        <div className="flex items-center justify-center">
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {t("common.or")}
                          </div>
                        </div>
                        <Button
                          type="button"
                          className="flex-1"
                          onClick={() => {
                            setIsTopicSelection(true);
                            setIsOutcomeSelection(false);
                            setCreatePrompt("");
                            setSelectedOutcomes([]);
                            setSelectedMaarifOutcomes([]);
                          }}
                          variant="solid"
                          size="md"
                          rounded="lg"
                          textColor={
                            isTopicSelection
                              ? "text-white"
                              : "text-gray-600 dark:text-gray-400"
                          }
                          bgColor={
                            isTopicSelection
                              ? "bg-gradient-to-r from-blue-600 to-teal-600 shadow-lg"
                              : "bg-gray-100 dark:bg-gray-700"
                          }
                        >
                          {t("common.topicSelection")}
                        </Button>
                      </>
                    ) : null}
                    {i18n.language !== "en" && (
                      <>
                        <div className="flex items-center justify-center">
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {t("common.or")}
                          </div>
                        </div>
                        <Button
                          type="button"
                          className="flex-1"
                          onClick={() => {
                            setIsTopicSelection(false);
                            setIsOutcomeSelection(true);
                            setCreatePrompt("");
                            setSelectedTopics([]);
                            const availableCourses = Object.entries(
                              CourseData,
                            ).filter(
                              ([, value]) =>
                                value.visible(gradeAsNumber) &&
                                value.hasOutcomes,
                            );
                            const firstAvailableCourse =
                              availableCourses[0]?.[0] as GetTopicsCourseType;
                            const doesPreviousCourseExist =
                              availableCourses.some(
                                ([key]) => key === courseName,
                              );
                            setCourseName(
                              doesPreviousCourseExist
                                ? courseName
                                : firstAvailableCourse,
                            );
                          }}
                          variant="solid"
                          size="md"
                          rounded="lg"
                          textColor={
                            isOutcomeSelection
                              ? "text-white"
                              : "text-gray-600 dark:text-gray-400"
                          }
                          bgColor={
                            isOutcomeSelection
                              ? "bg-gradient-to-r from-blue-600 to-teal-600 shadow-lg"
                              : "bg-gray-100 dark:bg-gray-700"
                          }
                        >
                          {isMaarif
                            ? t("common.maarifOutcomeSelection")
                            : t("common.outcomeSelection")}
                        </Button>
                      </>
                    )}
                  </div>
                )}

                <div className="space-y-4">
                  {isTopicSelection && !isJuniorGrade ? (
                    <UnitTopicSelector
                      grade={gradeLevel}
                      courseType={courseName}
                      selectedTopics={selectedTopics}
                      setSelectedTopics={(topicIds, topicNames) => {
                        setSelectedTopics(topicIds);
                        setCreatePrompt(
                          topicNames ? topicNames.join(", ") : "",
                        );
                      }}
                    />
                  ) : isOutcomeSelection && gradeLevel !== -1 ? (
                    <UnitOutcomeSelector
                      grade={gradeLevel}
                      courseType={courseName}
                      selectedOutcomes={selectedOutcomes}
                      isMaarif={isMaarif}
                      setSelectedOutcomes={(outcomeIds, outcomeTexts) => {
                        setSelectedOutcomes(outcomeIds);
                        setCreatePrompt(
                          outcomeTexts ? outcomeTexts.join(", ") : "",
                        );
                      }}
                    />
                  ) : (
                    <form onSubmit={handleCreateMindMap} className="space-y-4">
                      <div className="space-y-2">
                        <label className="inline-block text-sm font-medium text-blue-700 dark:text-blue-300">
                          {isJuniorGrade
                            ? t("hooks.form.promptJunior")
                            : t("hooks.form.lessonTopic")}
                        </label>
                        <div className="relative">
                          <Input
                            type="text"
                            // value={createPrompt}
                            value={
                              gradeAsNumber === -1 ? undefined : createPrompt
                            }
                            onChange={(e) => {
                              if (gradeLevel === -1) {
                                const newPrompt = processPrompt(
                                  e.target.value,
                                  createPrompt,
                                  i18n.language,
                                );
                                setCreatePrompt(newPrompt);
                              } else {
                                setCreatePrompt(e.target.value);
                              }
                            }}
                            classNames={{
                              input:
                                "w-full p-4 rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-800/80 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all",
                            }}
                            disabled={isCreatingMindMap}
                            placeholder={
                              isJuniorGrade
                                ? t("hooks.form.promptJuniorPlaceholder")
                                : placeholderTopic?.topic
                            }
                          />
                        </div>
                      </div>
                    </form>
                  )}
                </div>

                <Button
                  variant="solid"
                  data-testid="generate-mindmap-button"
                  size="lg"
                  rounded="xl"
                  fullWidth
                  bgColor="bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700"
                  textColor="text-white"
                  className="shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 focus:ring-4 focus:ring-blue-500/50"
                  disabled={
                    (isTopicSelection && selectedTopics.length === 0) ||
                    (isOutcomeSelection &&
                      selectedOutcomes.length === 0 &&
                      selectedMaarifOutcomes.length === 0) ||
                    (!isTopicSelection &&
                      !isOutcomeSelection &&
                      !createPrompt.trim()) ||
                    isCreatingMindMap
                  }
                  loading={isCreatingMindMap}
                  onClick={handleCreateMindMap}
                >
                  {isCreatingMindMap
                    ? t("mindmap.creatingButton")
                    : t("mindmap.createButton")}
                </Button>
              </div>
            ) : (
              <LoadingContainer
                showSteps={steps}
                title={t("mindmap.generating")}
                themeColor="blue"
              />
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {isLoadingMindMaps && (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500"></div>
          </div>
        )}

        {!isLoadingMindMaps &&
          (!mindMapsData?.data || mindMapsData.data.length === 0) && (
            <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4 bg-gray-50 dark:bg-gray-900/20 rounded-2xl p-8">
              <div className={`p-4 rounded-full bg-teal-500`}>
                <Package size={32} className={"text-white"} />
              </div>
              <Text variant={"h2"}>{t("mindmap.empty.title")}</Text>
              <Text variant="p" status="subtle" size={"base"}>
                {t("mindmap.empty.description")}
              </Text>
            </div>
          )}

        {mindMapsData?.data && mindMapsData.data.length > 0 && (
          <>
            <Text variant="h1" className="mb-8 text-center sm:text-left">
              {t("mindmap.previousMindMaps")}
            </Text>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {mindMapsData.data.map((mindMap) => (
                <Card
                  key={mindMap.id}
                  item={{
                    courseName: mindMap?.courseName || "",
                    createdAt: mindMap?.createdAt || "",
                    description: mindMap?.title || "",
                    gradeLevel:
                      mindMap?.gradeLevel === -1 ? 0 : mindMap?.gradeLevel || 0,
                    id: mindMap?.id?.toString() || "",
                    imageUrl: mindMap?.imageUrl || "",
                    prompt: mindMap?.prompt || "",
                    title: mindMap?.title || "",
                    videoUrl: mindMap?.videoUrl || "",
                  }}
                  selectItem={() => handleSelectMindMap(mindMap.id)}
                />
              ))}
            </div>
          </>
        )}

        {Boolean(shouldShowPagination) && (
          <Pagination
            currentPage={currentPage}
            totalPages={Number(mindMapsData?.totalPages || 1)}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    </div>
  );
}
